import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from '../../../../../../../shared/services/helpers/message-service/message.service';
import { ShootProofDataProvider } from '../../../../../../../shared/services/integrations/shootProof/shootProof.data.service';
import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SNModalService } from '@web-services/modal/snModal.service';
import { SelectExistingGalleryModal } from '@web-components/integrations/connect-galleries/modals/select-existing-gallery-modal/select-existing-gallery.modal';
import { CreateNewGalleryModal } from '@web-components/integrations/connect-galleries/modals/create-new-gallery-modal/create-new-gallery.modal';
import { PickTimeDataProvider } from '@services/integrations/pickTime/pickTime.data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import {
	GetPicTimeTemplates,
	Job,
	JobClient,
	JobMetadata,
	PicTimeClientInfo,
	PicTimeGalleriesForImport,
	ShootProofGalleriesBrand
} from '@models/models';
import { forkJoin } from 'rxjs/observable/forkJoin';

@Component({
	selector: 'add-gallery-modal',
	template: require('./add-gallery.modal.html'),
	styles: [require('./add-gallery.modal.scss')]
})
export class AddGalleryModal implements OnInit, OnDestroy {
	@Input('shootProofActions') shootProofActions;
	@Input('picTimeActions') picTimeActions;
	@Input('jobId') jobId;
	@Input('mainShootDate') mainShootDate;
	@Input('jobName') jobName;
	@Input('job') job: Job;
	shootProofBrands = [];
	picTimeGalleries = [];
	jobMetaData: JobMetadata;
	picTimeGalleriesMetadata: PicTimeGalleriesForImport;
	picTimeTemplates: GetPicTimeTemplates[];
	disableShootProofModals = false;
	disablePicTimeModals = false;
	createGalleryModalRef: NgbModalRef;
	selectGalleryModalRef: NgbModalRef;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private shootProofDataService: ShootProofDataProvider,
		private pickTimeDataProvider: PickTimeDataProvider,
		private picTimeDataResponse: PickTimeDataProvider,
		private messageService: MessageService,
		private activeModal: NgbActiveModal,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		if (this.shootProofActions.length) {
			this.getBrandsForGalleries();
		}
		if (this.picTimeActions.length) {
			this.initPicTime();
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	showSelectExistingShootProofGalleryModal() {
		this.activeModal.close();
		this.selectGalleryModalRef = this.modalService.openModal(
			SelectExistingGalleryModal,
			{
				windowClass: 'ng-bootstrap-modal-small'
			}
		);
		this.selectGalleryModalRef.componentInstance.shootProofBrands = this.shootProofBrands;
		this.selectGalleryModalRef.componentInstance.jobId = this.jobId;
	}

	showSelectExistingPicTimeGalleryModal() {
		this.activeModal.close();
		this.selectGalleryModalRef = this.modalService.openModal(
			SelectExistingGalleryModal,
			{
				windowClass: 'ng-bootstrap-modal-small pic-time-select-modal'
			}
		);
		this.selectGalleryModalRef.componentInstance.jobId = this.jobId;
		this.selectGalleryModalRef.componentInstance.picTimeGalleriesMetadata = this.picTimeGalleriesMetadata;
	}

	showCreateNewShootProofGalleryModal() {
		this.activeModal.close();
		this.createGalleryModalRef = this.modalService.openModal(
			CreateNewGalleryModal,
			{
				windowClass: 'ng-bootstrap-modal-small'
			}
		);
		this.createGalleryModalRef.componentInstance.shootProofBrands = this.shootProofBrands;
		this.createGalleryModalRef.componentInstance.jobId = this.jobId;
		this.createGalleryModalRef.componentInstance.mainShootDate = this.mainShootDate;
		this.createGalleryModalRef.componentInstance.jobName = this.jobName;
	}

	showCreateNewPicTimeGalleryModal(): void {
		this.activeModal.close();
		this.createGalleryModalRef = this.modalService.openModal(
			CreateNewGalleryModal,
			{
				windowClass: 'ng-bootstrap-modal-small modal-visible'
			}
		);
		this.createGalleryModalRef.componentInstance.jobId = this.jobId;
		this.createGalleryModalRef.componentInstance.mainShootDate = this.mainShootDate;
		this.createGalleryModalRef.componentInstance.jobMetaData = this.jobMetaData;
		this.createGalleryModalRef.componentInstance.picTimeTemplates = this.picTimeTemplates;
		this.createGalleryModalRef.componentInstance.clientList = this.createClientList(
			this.job
		);
	}

	initPicTime() {
		this.disablePicTimeModals = true;
		forkJoin([
			this.pickTimeDataProvider
				.getJobMetadata(this.jobId)
				.pipe(takeUntil(this.unsubscribe)),
			this.pickTimeDataProvider
				.getPicTimeTemplates()
				.pipe(takeUntil(this.unsubscribe)),
			this.pickTimeDataProvider
				.getImportPicTimeGalleries()
				.pipe(takeUntil(this.unsubscribe))
		]).subscribe(
			(data): void => {
				this.jobMetaData = data[0].data;
				this.picTimeTemplates = data[1].data;
				this.picTimeGalleriesMetadata = data[2].data;
			},
			(error): void => this.messageService.handleError(error),
			(): void => {
				this.disablePicTimeModals = false;
			}
		);
	}

	getBrandsForGalleries(): void {
		this.disableShootProofModals = true;
		this.shootProofDataService
			.getBrandsForGalleriesV1()
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.disableShootProofModals = false))
			)
			.subscribe(
				(data): ShootProofGalleriesBrand[] =>
					(this.shootProofBrands = data.data),
				(error): void => {
					this.messageService.handleError(error);
					this.activeModal.dismiss();
				}
			);
	}

	onDismiss(): void {
		this.activeModal.close();
	}

	private createClientList(job: Job): Array<PicTimeClientInfo> {
		const { primaryContact, secondaryContact } = job;
		const list = [];

		const addContractToList = (contact: JobClient): PicTimeClientInfo => ({
			name:
				contact.firstName + (contact.lastName ? ` ${contact.lastName}` : ''),
			email: contact.email
		});

		if (primaryContact) {
			list.push(addContractToList(primaryContact));
		}

		if (secondaryContact) {
			list.push(addContractToList(secondaryContact));
		}

		return list;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/integrations/connect-galleries/modals/add-gallery-modal/add-gallery.modal.ts